import React, { useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { api } from './api.js';
import JsonReceipt from './JsonReceipt.jsx';
import { safeSourceUrl, sourceJurisdiction } from './current-results.js';
import { useVisibilityMode } from './VisibilityMode.jsx';

const statusLabel = {
  decomposed: 'DECOMPOSED',
  held: 'HELD',
  not_decomposable: 'NEEDS BASE TEXT',
  non_law: 'NON-LAW',
  failed: 'FAILED',
};

const canonicalLayerLabels = {
  H: 'Help',
  W: 'Workflow',
  A: 'Accountability',
  O: 'Override',
  D: 'Definition',
};

const runtimeClassifierLabels = {
  R: 'Non-canonical rule classifier',
};

const layerLabels = {
  ...canonicalLayerLabels,
  ...runtimeClassifierLabels,
};

function representationText(item) {
  if (item?.semantic_state === 'deleted') return item?.raw_text || '';
  return item?.effective_text ?? item?.raw_text ?? '';
}

function semanticRepresentations(detail) {
  const units = Array.isArray(detail?.units) ? detail.units : [];
  return units.flatMap((unit) => {
    const layers = Array.isArray(unit.layers) ? unit.layers : [];
    if (layers.length) {
      return layers.map((layer) => ({
        ...unit,
        semantic_layer: layer,
        semantic_label: layerLabels[layer] || layer,
        semantic_state: 'classified',
      }));
    }
    if (unit.disposition === 'STRUCT') {
      return [{ ...unit, semantic_layer: null, semantic_label: 'Structure block', semantic_state: 'structure' }];
    }
    if (unit.disposition === 'DELETED') {
      return [{ ...unit, semantic_layer: null, semantic_label: 'Deleted source span', semantic_state: 'deleted' }];
    }
    return [{ ...unit, semantic_layer: null, semantic_label: 'Unresolved source span', semantic_state: 'unresolved' }];
  });
}

function semanticSummary(detail) {
  const reps = semanticRepresentations(detail);
  return {
    representations: reps,
    classified: reps.filter((item) => item.semantic_state === 'classified').length,
    structure: reps.filter((item) => item.semantic_state === 'structure').length,
    unresolved: reps.filter((item) => item.semantic_state === 'unresolved').length,
    deleted: reps.filter((item) => item.semantic_state === 'deleted').length,
  };
}


function tone(status) {
  if (status === 'failed') return 'bad';
  if (status === 'held' || status === 'not_decomposable') return 'warn';
  if (status === 'decomposed') return 'good';
  return 'neutral';
}

function resultStatus(detail) {
  if (!detail) return 'failed';
  if (detail.hold_receipt) return 'held';
  if (!detail.decomposition && detail.failure_receipts?.length) return 'failed';
  return detail.decomposition?.outcome || 'failed';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function printableReport(detail, includeAdmin = false) {
  const source = detail.source || {};
  const result = detail.decomposition || {};
  const units = Array.isArray(detail.units) ? detail.units : [];
  const sections = Array.isArray(detail.sections) ? detail.sections : [];
  const status = resultStatus(detail);
  const semantic = semanticSummary(detail);
  const representationHtml = semantic.representations.map((item) => `
    <article>
      <h3>${escapeHtml(item.semantic_label)}</h3>
      <p><b>State:</b> ${escapeHtml(item.semantic_state)}</p>
      <p><b>Section:</b> ${escapeHtml(item.section_ord ?? '—')}</p>
      <pre>${escapeHtml(representationText(item))}</pre>
      ${includeAdmin ? `<p><b>Underlying storage ordinal:</b> ${escapeHtml(item.unit_ord)} · <b>kind:</b> ${escapeHtml(item.kind || 'structure')} · <b>offsets:</b> ${escapeHtml(item.raw_start)}–${escapeHtml(item.raw_end)}</p>` : ''}
    </article>`).join('');

  const adminHtml = includeAdmin ? `
    <section><h2>Administrative evidence</h2>
      <h3>Runtime target</h3><pre>${escapeHtml(JSON.stringify(detail.target || {}, null, 2))}</pre>
      <h3>Decomposition receipt</h3><pre>${escapeHtml(JSON.stringify(result, null, 2))}</pre>
      <h3>Sections</h3><pre>${escapeHtml(JSON.stringify(sections, null, 2))}</pre>
      <h3>Hold receipt</h3><pre>${escapeHtml(JSON.stringify(detail.hold_receipt || null, null, 2))}</pre>
      <h3>Failure receipts</h3><pre>${escapeHtml(JSON.stringify(detail.failure_receipts || [], null, 2))}</pre>
      <h3>Operative-text events</h3><pre>${escapeHtml(JSON.stringify(detail.markup_events || [], null, 2))}</pre>
    </section>` : '';

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>Rosetta detailed report</title>
<style>
  body{font-family:Georgia,serif;max-width:1100px;margin:32px auto;padding:0 24px;color:#171713}
  h1,h2,h3{font-weight:600} .mono,pre{font-family:ui-monospace,SFMono-Regular,Menlo,monospace}
  pre{white-space:pre-wrap;overflow-wrap:anywhere;border:1px solid #bbb;padding:14px;background:#fafafa}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:24px}.meta{font-size:14px}
  article{break-inside:avoid;border-top:1px solid #ccc;padding:12px 0}
  @media(max-width:800px){.grid{grid-template-columns:1fr}}
  @media print{body{max-width:none;margin:0}.no-print{display:none}}
</style></head><body>
  <header>
    <p>ROSETTA · DETAILED SOURCE-BOUND REPORT</p>
    <h1>${escapeHtml(source.document_name || source.document_identifier || source.source_content_id)}</h1>
    <p class="meta">Status: ${escapeHtml(statusLabel[status] || status)} · Runtime: ${escapeHtml(detail.target?.ruleset_version || 'unknown')}</p>
    <p class="mono">Source: ${escapeHtml(source.source_content_id || '')}<br>Source SHA-256: ${escapeHtml(source.source_content_hash || '')}<br>Ruleset SHA-256: ${escapeHtml(detail.target?.ruleset_sha256 || '')}</p>
  </header>
  <section class="grid">
    <div><h2>Original source</h2><pre>${escapeHtml(source.source_text || 'Source text unavailable.')}</pre></div>
    <div><h2>Decomposition summary</h2>
      <p><b>Outcome:</b> ${escapeHtml(result.outcome || 'No decomposition receipt')}</p>
      <p><b>Sections:</b> ${escapeHtml(result.section_count ?? sections.length)}</p>
      <p><b>Layer representations:</b> ${escapeHtml(semantic.classified)}</p>
      <p><b>Structure blocks:</b> ${escapeHtml(semantic.structure)}</p>
      <p><b>Unresolved spans:</b> ${escapeHtml(semantic.unresolved)}</p>
      <p><b>Deleted spans:</b> ${escapeHtml(semantic.deleted)}</p>
      <p><b>Layers:</b> ${escapeHtml(JSON.stringify(result.layer_counts || {}))}</p>
      ${result.reason ? `<p><b>Reason:</b> ${escapeHtml(result.reason)}</p>` : ''}
      ${detail.hold_receipt ? `<p><b>Hold:</b> ${escapeHtml(detail.hold_receipt.gate)} — ${escapeHtml(detail.hold_receipt.reason)}</p>` : ''}
    </div>
  </section>
  <section><h2>Source-bound decomposition</h2>${representationHtml || '<p>No source-bound representations recorded.</p>'}</section>
  ${adminHtml}
</body></html>`;
}

function downloadHtml(filename, html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function csvCell(value) {
  const text = String(value ?? '');
  return '"' + text.replaceAll('"', '""') + '"';
}

function downloadCsv(filename, rows) {
  const body = rows.map((row) => row.map(csvCell).join(',')).join('\n') + '\n';
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function semanticCsv(detail) {
  const source = detail.source || {};
  const rows = [[
    'document_identifier','document_name','source_content_id','source_content_hash',
    'section','unit','layer','state','raw_start','raw_end','text'
  ]];
  semanticRepresentations(detail).forEach((item) => rows.push([
    source.document_identifier || '',
    source.document_name || '',
    source.source_content_id || '',
    source.source_content_hash || '',
    item.section_ord ?? '',
    item.unit_ord ?? '',
    item.semantic_label || '',
    item.semantic_state || '',
    item.raw_start ?? '',
    item.raw_end ?? '',
    representationText(item),
  ]));
  return rows;
}

function highlightedSource(sourceText, item) {
  const text=String(sourceText || '');
  if(!item || !Number.isInteger(item.raw_start) || !Number.isInteger(item.raw_end) || item.raw_start < 0 || item.raw_end < item.raw_start || item.raw_start >= text.length) {
    return <>{text || 'Full source text unavailable.'}</>;
  }
  const end=Math.min(text.length,item.raw_end);
  return <>{text.slice(0,item.raw_start)}<mark className="source-alignment-mark">{text.slice(item.raw_start,end)}</mark>{text.slice(end)}</>;
}

function printHtml(html) {
  const target = window.open('', '_blank');
  if (!target) return;
  target.opener = null;
  target.document.open();
  target.document.write(html);
  target.document.close();
  target.focus();
  window.setTimeout(() => target.print(), 250);
}

function Target({ value }) {
  const target=value?.target || {};
  return <div className="panel">
    <span className="eyebrow">SELECTED RUNTIME</span>
    <h2>{target.ruleset_version || 'No runtime selected'}</h2>
    <p className="mono">{target.ruleset_sha256 || '—'}</p>
    <p>{target.identity_valid ? 'Installed fingerprint matches selection.' : 'RUNTIME IDENTITY MISMATCH'}</p>
  </div>;
}

function RunState({ value }) {
  const run=value?.run || {state:'idle'};
  return <section className="panel">
    <div className="section-heading">
      <div><span className="eyebrow">LIVE EXECUTION</span><h2>{String(run.state || 'idle').toUpperCase()}</h2></div>
      {run.run_id ? <span className="mono">{run.run_id}</span> : null}
    </div>
    <div className="live-result-summary">
      <div><span>Target bills</span><strong>{run.max_bills ?? 0}</strong></div>
      <div><span>Completed</span><strong>{run.bills_done ?? 0}</strong></div>
      <div><span>Failed</span><strong>{run.bills_failed ?? 0}</strong></div>
      <div><span>Remaining</span><strong>{run.remaining ?? 0}</strong></div>
    </div>
    {run.message ? <p className="error-panel">{run.message}</p> : null}
  </section>;
}

function useOverview(params='limit=50') {
  const [state,setState]=useState({loading:true});
  useEffect(()=>{
    let active=true;
    let timer;
    const load=async()=>{
      try {
        const data=await api('/api/live-rule?'+params);
        if(active) setState({loading:false,data});
        const delay=data?.run?.state==='running' ? 2000 : 8000;
        if(active) timer=setTimeout(load,delay);
      } catch(error) {
        if(active) setState({loading:false,error});
        if(active) timer=setTimeout(load,8000);
      }
    };
    load();
    return ()=>{active=false;if(timer)clearTimeout(timer);};
  },[params]);
  return state;
}

function Cards({ rows=[] }) {
  if(!rows.length) return <div className="empty-state"><strong>No results for the selected runtime yet.</strong><p>Historical engine output is not substituted into this surface.</p></div>;
  return <div className="run-grid">{rows.map(row=><article className="run-card" key={row.source_content_id}>
    <div className="run-card-topline">
      <span className="mono">{sourceJurisdiction(row) || 'Jurisdiction not recorded'}</span>
      <span className={'badge badge-'+tone(row.status)}>{statusLabel[row.status] || String(row.status).toUpperCase()}</span>
    </div>
    <h3>{row.document_name || row.document_identifier || row.source_content_id}</h3>
    {row.document_identifier ? <p className="mono">{row.document_identifier}</p> : null}
    <div className="current-card-structure">
      <span>{row.section_count ?? 0} sections</span>
      <span>{row.unit_count ?? 0} units</span>
      {Object.entries(row.layer_counts || {}).filter(([, count]) => Number(count) > 0).map(([layer, count]) => (
        <span key={layer}>{layerLabels[layer] || layer} {count}</span>
      ))}
    </div>
    {row.reason ? <p>{row.reason}</p> : null}
    <NavLink className="button result-open" to={'/review/'+row.source_content_id}>Inspect law →</NavLink>
  </article>)}</div>;
}

export function CurrentIntakeResultTabs({ sourceContentId, originalText='' }) {
  const [active,setActive]=useState('original');
  const [state,setState]=useState({loading:false,data:null,error:null});

  useEffect(()=>{
    if(!sourceContentId) return;
    let mounted=true;
    setState({loading:true,data:null,error:null});
    api('/api/live-rule/'+encodeURIComponent(sourceContentId))
      .then(data=>mounted&&setState({loading:false,data,error:null}))
      .catch(error=>mounted&&setState({loading:false,data:null,error}));
    return()=>{mounted=false;};
  },[sourceContentId]);

  const detail=state.data;
  const reps=detail ? semanticRepresentations(detail) : [];
  const tabs=[
    ['original','Original Text'],
    ['H','Help'],
    ['W','Workflow'],
    ['A','Accountability'],
    ['O','Overrides'],
    ['D','Definitions'],
    ['R','Classifier (non-canonical)'],
  ];

  return <section className="panel intake-result-tabs">
    <div className="section-heading">
      <div><span className="eyebrow">CURRENT INTAKE RESULT</span><h2>Verify source and decomposition</h2></div>
      <NavLink className="button" to={'/review/'+encodeURIComponent(sourceContentId)}>Open full current law →</NavLink>
    </div>
    <div className="intake-tab-rail">
      {tabs.map(([key,label])=><button type="button" key={key} className={active===key?'active':''} onClick={()=>setActive(key)}>{label}</button>)}
    </div>

    {active==='original' ? <div className="intake-source-verification">
      <span className="eyebrow">PRESERVED EXTRACTED TEXT</span>
      <pre>{originalText || detail?.source?.source_text || 'Extracted text unavailable.'}</pre>
    </div> : null}

    {active!=='original' && state.loading ? <div className="loading-panel">Loading selected-runtime result…</div> : null}
    {state.error ? <div className="error-panel">{state.error.message}</div> : null}
    {active!=='original' && detail ? <div className="substrate-object-list">
      {reps.filter(item=>item.semantic_layer===active && item.semantic_state==='classified').map((item,index)=><article className="substrate-object" key={`${item.unit_ord}-${active}-${index}`}>
        <div className="object-topline"><strong>{item.semantic_label}</strong><span className="mono">#{item.unit_ord ?? index+1}</span></div>
        <p>{representationText(item)}</p>
        <small className="mono">section {item.section_ord ?? '—'} · source {item.raw_start ?? '—'}–{item.raw_end ?? '—'}</small>
      </article>)}
      {!reps.some(item=>item.semantic_layer===active && item.semantic_state==='classified') ? <div className="empty-state">No current selected-runtime {layerLabels[active]} representation recorded.</div> : null}
    </div> : null}

    <p className="intake-tab-boundary">Historical confidence scores and signal-status fields are not shown because the current V10 read contract does not expose those values.</p>
  </section>;
}

export function LiveDashboard() {
  const state=useOverview('limit=18');
  if(state.error) return <div className="error-panel">{state.error.message}</div>;
  const data=state.data;
  const rows=data?.rows || [];
  const layerTotals=rows.reduce((acc,row)=>{
    Object.entries(row.layer_counts || {}).forEach(([layer,count])=>{
      acc[layer]=(acc[layer] || 0)+Number(count || 0);
    });
    return acc;
  },{});
  const unitTotal=rows.reduce((sum,row)=>sum+Number(row.unit_count || 0),0);
  const sectionTotal=rows.reduce((sum,row)=>sum+Number(row.section_count || 0),0);
  const pipeline=[
    ['01','Exact source'],
    ['02','Preflight'],
    ['03','Operative text'],
    ['04','Sections'],
    ['05','H/W/A/O/D'],
    ['06','Receipt'],
  ];

  return <>
    <header className="page-header live-page-header">
      <div>
        <div className="eyebrow">ROSETTA / CURRENT V10 OPERATING SURFACE</div>
        <h1>Translate law into inspectable operating structure.</h1>
        <p>The current dashboard restores the original Rosetta operating view using only the selected runtime. Source identity, decomposition structure, coverage, holds, and receipts stay visible without substituting historical generations.</p>
      </div>
      <div className="page-actions">
        <NavLink className="button button-primary" to="/intake">Process a law</NavLink>
        <NavLink className="button" to="/layers">Inspect layers</NavLink>
      </div>
    </header>

    {!data ? <div className="loading-panel">Loading selected runtime…</div> : <>
      <div className="dashboard-capability-grid">
        <article className="metric-card"><span>Current sources</span><strong>{data.source_total ?? 0}</strong><small>selected-runtime source revisions</small></article>
        <article className="metric-card"><span>Decomposed</span><strong>{data.counts?.decomposed ?? 0}</strong><small>source-bound current results</small></article>
        <article className="metric-card"><span>Held</span><strong>{data.counts?.held ?? 0}</strong><small>bounded stop receipts</small></article>
        <article className="metric-card"><span>Needs base text</span><strong>{data.counts?.not_decomposable ?? 0}</strong><small>amendment-only sources awaiting their base law</small></article>
        <article className="metric-card"><span>Failed</span><strong>{data.counts?.failed ?? 0}</strong><small>current runtime failures</small></article>
        <article className="metric-card"><span>Sections in view</span><strong>{sectionTotal}</strong><small>across the latest {rows.length} results</small></article>
        <article className="metric-card"><span>Units in view</span><strong>{unitTotal}</strong><small>current decomposition units</small></article>
      </div>

      <section className="panel resurrected-pipeline">
        <div className="section-heading">
          <div><span className="eyebrow">CURRENT PIPELINE</span><h2>LAW TEXT → CURRENT ROSETTA RECEIPT</h2></div>
          <span className="live-marker">● selected runtime</span>
        </div>
        <div className="pipeline-track">
          {pipeline.map(([num,label],index)=><React.Fragment key={num}>
            <div className="pipeline-node"><span>{num}</span><strong>{label}</strong></div>
            {index < pipeline.length-1 ? <div className="pipeline-connector" /> : null}
          </React.Fragment>)}
        </div>
        <div className="dashboard-layer-strip">
          {Object.entries(canonicalLayerLabels).map(([key,label])=><div key={key}><span>{label}</span><strong>{layerTotals[key] || 0}</strong></div>)}
        </div>
        <div className="gap-notice"><strong>Non-canonical runtime classifier</strong><p>V10 also recorded {layerTotals.R || 0} R classifications. R is preserved for inspection, but it is not a sixth canonical Rosetta layer.</p></div>
      </section>

      <section className="panel">
        <div className="section-heading"><div><span className="eyebrow">UNRESOLVED CURRENT-OWNER CAPABILITIES</span><h2>Actors · References · Procedures · Deadlines · Appeals</h2></div></div>
        <p>These capabilities remain explicit gaps, not inferred data. No historical row is substituted into this V10 view.</p>
        <div className="gap-notice"><strong>CURRENT OWNER / PROJECTION REQUIRED</strong><p>Only independently certified selected-runtime source-bound contracts can populate these registries.</p></div>
      </section>

      <section className="panel extraction-monitor">
        <div className="section-heading">
          <div><span className="eyebrow">EXTRACTION MONITOR</span><h2>Current source-bound results</h2></div>
          <NavLink className="button" to="/review">Open all current laws →</NavLink>
        </div>
        <div className="monitor-table">
          <div className="monitor-row monitor-head"><span>Source</span><span>Status</span><span>Sections</span><span>Units</span><span>Coverage</span></div>
          {rows.map(row=><NavLink className="monitor-row" to={'/review/'+row.source_content_id} key={row.source_content_id}>
            <span><strong>{row.document_name || row.document_identifier || 'Source'}</strong><small className="mono">{row.document_identifier || row.source_content_id}</small></span>
            <span className={'badge badge-'+tone(row.status)}>{statusLabel[row.status] || row.status}</span>
            <span>{row.section_count ?? 0}</span>
            <span>{row.unit_count ?? 0}</span>
            <span className="monitor-layers">{Object.entries(row.layer_counts || {}).filter(([,count])=>Number(count)>0).map(([layer,count])=>`${layer}:${count}`).join(' · ') || '—'}</span>
          </NavLink>)}
        </div>
      </section>

      <div className="two-column"><Target value={data}/><RunState value={data}/></div>
    </>}
  </>;
}

export function LivePipeline() {
  const state=useOverview('limit=50');
  const data=state.data;
  const rows=data?.rows || [];
  if(state.error) return <div className="error-panel">{state.error.message}</div>;
  return <div className="current-results">
    <header className="page-header">
      <div>
        <div className="eyebrow">DOCUMENT PIPELINE / SELECTED RUNTIME</div>
        <h1>Current source → structure execution.</h1>
        <p>This monitor follows the selected runtime only. It shows the current execution receipt and exact source-result population without substituting historical extraction runs.</p>
      </div>
    </header>
    {!data ? <div className="loading-panel">Loading current pipeline…</div> : <>
      <div className="realtime-receipt">
        <span className="live-dot" />
        <strong>AUTO-REFRESH</strong>
        <span>{data.run?.state==='running' ? '2 second cadence while running' : '8 second cadence while idle'}</span>
        <small>{data.observed_at ? `observed ${new Date(data.observed_at).toLocaleString()}` : 'observation time unavailable'}</small>
      </div>
      <RunState value={data}/>
      <section className="panel resurrected-pipeline">
        <div className="section-heading"><div><span className="eyebrow">CURRENT EXECUTION PATH</span><h2>LAW TEXT → SOURCE RECEIPT → OPERATIVE TEXT → STRUCTURE → H/W/A/O/D → RESULT RECEIPT</h2></div></div>
        <div className="pipeline-track">
          {[
            ['01','Law text'],['02','Source receipt'],['03','Operative text'],
            ['04','Structure'],['05','H/W/A/O/D'],['06','Result receipt'],
          ].map(([num,label],index,all)=><React.Fragment key={num}>
            <div className="pipeline-node"><span>{num}</span><strong>{label}</strong></div>
            {index < all.length-1 ? <div className="pipeline-connector" /> : null}
          </React.Fragment>)}
        </div>
      </section>
      <section className="panel extraction-monitor">
        <div className="section-heading">
          <div><span className="eyebrow">CURRENT RESULT RECEIPTS</span><h2>{data.source_total ?? 0} selected-runtime source revisions</h2></div>
          <span>{rows.length} loaded</span>
        </div>
        <div className="monitor-table">
          <div className="monitor-row monitor-head"><span>Exact source</span><span>Status</span><span>Sections</span><span>Units</span><span>Layer receipt</span></div>
          {rows.map(row=><NavLink className="monitor-row" to={'/review/'+row.source_content_id} key={row.source_content_id}>
            <span><strong>{row.document_name || row.document_identifier || row.source_content_id}</strong><small className="mono">{row.document_identifier || row.source_content_id}</small></span>
            <span className={'badge badge-'+tone(row.status)}>{statusLabel[row.status] || row.status}</span>
            <span>{row.section_count ?? 0}</span>
            <span>{row.unit_count ?? 0}</span>
            <span className="monitor-layers">{Object.entries(row.layer_counts || {}).filter(([,count])=>Number(count)>0).map(([layer,count])=>`${layer}:${count}`).join(' · ') || '—'}</span>
          </NavLink>)}
        </div>
      </section>
      <JsonReceipt
        value={{
          contract:data.contract,
          observed_at:data.observed_at,
          target:data.target,
          run:data.run,
          source_total:data.source_total,
          counts:data.counts,
          rows,
        }}
        filename="rosetta-current-pipeline-receipt.json"
        label="Current pipeline receipt (JSON)"
      />
    </>}
  </div>;
}

export function LiveResults({title='Current laws',eyebrow='CURRENT OPERATIONAL RECORD',fixedStatus=''}) {
  const { searchQuery }=useParams();
  const routeSearch=searchQuery || '';
  const [query,setQuery]=useState(routeSearch);
  const [search,setSearch]=useState(routeSearch);
  const [status,setStatus]=useState(fixedStatus);
  useEffect(()=>{
    if(routeSearch !== search) {
      setQuery(routeSearch);
      setSearch(routeSearch);
      setStart(1);
    }
  },[routeSearch]);
  const [start,setStart]=useState(1);
  const params=new URLSearchParams({start:String(start),limit:'50'});
  if(search)params.set('q',search);
  if(fixedStatus||status)params.set('status',fixedStatus||status);
  const state=useOverview(params.toString());
  const data=state.data;
  return <div className="current-results">
    <header className="page-header"><div><div className="eyebrow">{eyebrow}</div><h1>{title}</h1><p>Selected-runtime results only. The UI does not bind to a generation name and does not substitute historical output.</p></div></header>
    {data ? <><Target value={data}/><RunState value={data}/></> : null}
    <form className="result-search panel" onSubmit={e=>{e.preventDefault();setSearch(query.trim());setStart(1);}}>
      <label>Result status<select value={fixedStatus||status} disabled={Boolean(fixedStatus)} onChange={e=>{setStatus(e.target.value);setStart(1);}}>
        <option value="">All statuses</option><option value="decomposed">Decomposed</option><option value="held">Held</option><option value="not_decomposable">Needs base text</option><option value="non_law">Non-law</option><option value="failed">Failed</option>
      </select></label>
      <label className="result-query">Find a source<input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Title, bill number, source id, or keyword"/></label>
      <button className="button button-primary" type="submit">Search</button>
    </form>
    {state.error ? <div className="error-panel">{state.error.message}</div> : null}
    {!data ? <div className="loading-panel">Loading selected runtime…</div> : <>
      <div className="live-result-summary">
        <div><span>Current results</span><strong>{data.source_total ?? 0}</strong></div>
        <div><span>Decomposed</span><strong>{data.counts?.decomposed ?? 0}</strong></div>
        <div><span>Held</span><strong>{data.counts?.held ?? 0}</strong></div>
        <div><span>Failed</span><strong>{data.counts?.failed ?? 0}</strong></div>
      </div>
      <section className="panel"><div className="section-heading"><h2>{data.page?.matching_total ?? 0} matching source revisions</h2><small>{data.observed_at ? new Date(data.observed_at).toLocaleString() : ''}</small></div><Cards rows={data.rows}/>
        <div className="page-actions result-pagination"><button className="button" disabled={start===1} onClick={()=>setStart(Math.max(1,start-50))}>Previous</button><button className="button" disabled={!data.page?.next_start} onClick={()=>setStart(data.page.next_start)}>Next</button></div>
      </section>
    </>}
  </div>;
}

export function LiveLayerViewer() {
  const { sourceId }=useParams();
  const overview=useOverview('limit=50&status=decomposed');
  const rows=overview.data?.rows || [];
  const [selectedId,setSelectedId]=useState(sourceId || null);
  const [active,setActive]=useState('sources');
  const [detailState,setDetailState]=useState({loading:false,data:null,error:null});

  useEffect(()=>{
    if(sourceId && sourceId !== selectedId) {
      setSelectedId(sourceId);
      return;
    }
    if(!selectedId && rows[0]?.source_content_id) setSelectedId(rows[0].source_content_id);
  },[sourceId,selectedId,rows]);

  useEffect(()=>{
    if(!selectedId) return;
    let mounted=true;
    setDetailState({loading:true,data:null,error:null});
    api('/api/live-rule/'+encodeURIComponent(selectedId))
      .then(data=>mounted&&setDetailState({loading:false,data,error:null}))
      .catch(error=>mounted&&setDetailState({loading:false,data:null,error}));
    return()=>{mounted=false;};
  },[selectedId]);

  const detail=detailState.data;
  const reps=detail ? semanticRepresentations(detail) : [];
  const tabDefs=[
    ['sources','Sources'],
    ['structure','Raw Structure'],
    ['H','Help'],
    ['W','Workflow'],
    ['A','Accountability'],
    ['O','Overrides'],
    ['D','Definitions'],
    ['coverage','Coverage + Validation'],
  ];

  function renderRows(items){
    if(!items.length) return <div className="empty-state">No current selected-runtime rows exist for this surface.</div>;
    return <div className="substrate-object-list">{items.map((item,index)=><article className="substrate-object" key={`${item.unit_ord || 'u'}-${item.semantic_layer || item.kind || 'row'}-${index}`}>
      <div className="object-topline"><strong>{item.semantic_label || item.kind || 'Unit'}</strong><span className="mono">#{item.unit_ord ?? index+1}</span></div>
      <p>{representationText(item) || item.raw_text || 'No display text.'}</p>
      <small className="mono">section {item.section_ord ?? '—'} · offsets {item.raw_start ?? '—'}–{item.raw_end ?? '—'}</small>
    </article>)}</div>;
  }

  return <div className="current-results">
    <header className="page-header">
      <div>
        <div className="eyebrow">LAYER VIEWER / CURRENT V10 SUBSTRATE</div>
        <h1>Canonical Rosetta Substrate</h1>
        <p>The recovered viewer exposes current selected-runtime source, structure, semantic layers, coverage, and explicit gaps. Historical tables are not silently promoted into current authority.</p>
      </div>
    </header>

    {overview.error ? <div className="error-panel">{overview.error.message}</div> : null}
    {!overview.data ? <div className="loading-panel">Loading current substrate…</div> : <div className="layer-layout">
      <aside className="panel layer-nav">
        {tabDefs.map(([key,label])=><button className={active===key?'active':''} key={key} type="button" onClick={()=>setActive(key)}>
          <span>{label}</span>
          <small>{key.length===1 ? detail?.decomposition?.layer_counts?.[key] ?? 0 : ''}</small>
        </button>)}
      </aside>
      <section className="layer-content">
        {active==='sources' ? <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">CURRENT SOURCES</span><h2>Selected-runtime source revisions</h2></div><span>{rows.length} loaded</span></div>
          <div className="source-picker">
            {rows.map(row=><button type="button" className={selectedId===row.source_content_id?'active':''} key={row.source_content_id} onClick={()=>setSelectedId(row.source_content_id)}>
              <strong>{row.document_name || row.document_identifier || row.source_content_id}</strong>
              <span className="mono">{row.document_identifier || row.source_content_id}</span>
              <small>{row.section_count ?? 0} sections · {row.unit_count ?? 0} units</small>
            </button>)}
          </div>
        </section> : null}

        {active!=='sources' && detailState.loading ? <div className="loading-panel">Loading exact current source…</div> : null}
        {detailState.error ? <div className="error-panel">{detailState.error.message}</div> : null}

        {detail && active==='structure' ? <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">RAW STRUCTURE</span><h2>{detail.source?.document_name || detail.source?.document_identifier}</h2></div><span>{detail.sections?.length || 0} sections · {detail.units?.length || 0} units</span></div>
          <div className="substrate-sections">
            {(detail.sections || []).map((section,index)=><article key={section.section_ord || index}><strong>Section {section.section_ord ?? index+1}</strong><pre>{section.heading_text || section.raw_text || section.text || JSON.stringify(section,null,2)}</pre></article>)}
          </div>
          <details className="comparison-source-evidence"><summary>Underlying current units</summary><pre>{JSON.stringify(detail.units || [],null,2)}</pre></details>
        </section> : null}

        {detail && ['H','W','A','O','D'].includes(active) ? <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">CURRENT CANONICAL LAYER</span><h2>{layerLabels[active]}</h2></div><span>{detail.decomposition?.layer_counts?.[active] ?? 0} representations</span></div>
          {renderRows(reps.filter(item=>item.semantic_layer===active))}
        </section> : null}

        {detail && active==='coverage' ? <section className="panel">
          <div className="section-heading"><div><span className="eyebrow">COVERAGE + VALIDATION</span><h2>Current selected-runtime coverage</h2></div><span className={'badge badge-'+tone(resultStatus(detail))}>{statusLabel[resultStatus(detail)] || resultStatus(detail)}</span></div>
          <div className="coverage-grid">
            {Object.entries(canonicalLayerLabels).map(([key,label])=><article className="coverage-card" key={key}><span>{label}</span><strong>{detail.decomposition?.layer_counts?.[key] ?? 0}</strong><p>{Number(detail.decomposition?.layer_counts?.[key] || 0)>0?'Current selected-runtime representations recorded.':'No current selected-runtime representation recorded.'}</p></article>)}
          </div>
          <div className="gap-notice"><strong>Non-canonical runtime classifier</strong><p>R count: {detail.decomposition?.layer_counts?.R ?? 0}. Preserved as runtime output; not counted as a canonical layer.</p></div>
          {detail.hold_receipt ? <div className="gap-notice"><strong>Hold receipt</strong><p>{detail.hold_receipt.reason}</p></div> : null}
          {detail.decomposition?.flags ? <details className="comparison-source-evidence"><summary>Current decomposition flags</summary><pre>{JSON.stringify(detail.decomposition.flags,null,2)}</pre></details> : null}
        </section> : null}

      </section>
    </div>}
  </div>;
}

export function LiveMultiLawProof() {
  const state=useOverview('limit=100');
  const data=state.data;
  const rows=data?.rows || [];
  const production=rows.filter(row=>row.document_type!=='test_control' && row.status==='decomposed');
  const controls=rows.filter(row=>row.document_type==='test_control');
  const layers=Object.entries(canonicalLayerLabels);
  const proofCsv=[
    ['class','document_identifier','document_name','source_content_id','source_content_hash','status','sections','units',...layers.map(([,label])=>label)],
    ...production.map(row=>[
      'production',row.document_identifier || '',row.document_name || '',row.source_content_id || '',
      row.source_content_hash || '',row.status || '',row.section_count ?? '',row.unit_count ?? '',
      ...layers.map(([key])=>row.layer_counts?.[key] ?? 0),
    ]),
    ...controls.map(row=>[
      'control',row.document_identifier || '',row.document_name || '',row.source_content_id || '',
      row.source_content_hash || '',row.status || '',row.section_count ?? '',row.unit_count ?? '',
      ...layers.map(([key])=>row.layer_counts?.[key] ?? 0),
    ]),
  ];
  if(state.error) return <div className="error-panel">{state.error.message}</div>;
  return <div className="current-results">
    <header className="page-header">
      <div><div className="eyebrow">MULTI-LAW PROOF / CURRENT V10</div><h1>One grammar across current laws.</h1><p>This matrix compares the selected runtime's current decomposition coverage across exact source revisions. Production laws and test controls are kept separate; historical engine output is not imported.</p></div>
      <div className="page-actions">
        <button className="button" type="button" onClick={()=>downloadCsv('rosetta-current-v10-multi-law-proof.csv',proofCsv)}>Export proof matrix (CSV)</button>
      </div>
    </header>
    {!data ? <div className="loading-panel">Loading current cross-law proof…</div> : <>
      <div className="dashboard-capability-grid proof-counts">
        <article className="metric-card"><span>Production proof rows loaded</span><strong>{production.length}</strong><small>decomposed non-control sources on this current page</small></article>
        <article className="metric-card"><span>Control receipts loaded</span><strong>{controls.length}</strong><small>test_control sources kept outside production proof</small></article>
        <article className="metric-card"><span>Current result population</span><strong>{data.source_total ?? 0}</strong><small>all selected-runtime result states</small></article>
      </div>
      <section className="panel">
        <div className="section-heading"><div><span className="eyebrow">CURRENT PRODUCTION COVERAGE MATRIX</span><h2>{production.length} decomposed production source revisions loaded</h2></div><span>{data.page?.matching_total ?? data.source_total ?? 0} matching current results</span></div>
        <div className="proof-matrix-wrap">
          <table className="proof-matrix">
            <thead><tr><th>Law / exact source</th><th>Sections</th><th>Units</th>{layers.map(([key,label])=><th key={key}>{label}</th>)}</tr></thead>
            <tbody>{production.map(row=><tr key={row.source_content_id}>
              <td><NavLink to={'/review/'+row.source_content_id}><strong>{row.document_name || row.document_identifier || row.source_content_id}</strong></NavLink><small className="mono">{row.document_identifier || row.jurisdiction_code || ''}</small><small className="mono">SHA {row.source_content_hash || 'not returned'}</small></td>
              <td>{row.section_count ?? 0}</td><td>{row.unit_count ?? 0}</td>
              {layers.map(([key])=><td key={key} className={Number(row.layer_counts?.[key] || 0)>0?'proof-populated':'proof-empty'}>{row.layer_counts?.[key] ?? 0}</td>)}
            </tr>)}</tbody>
          </table>
        </div>
        {!production.length ? <div className="empty-state">No decomposed production sources are loaded in this current page.</div> : null}
      </section>

      <section className="panel control-proof-panel">
        <div className="section-heading"><div><span className="eyebrow">CONTROL RECEIPTS</span><h2>Separated from production proof</h2></div><span>{controls.length} loaded</span></div>
        {!controls.length ? <div className="empty-state">No selected-runtime test controls are present in this loaded page. No historical control receipt is substituted.</div> :
          <div className="source-picker">{controls.map(row=><NavLink className="proof-control-card" key={row.source_content_id} to={'/review/'+row.source_content_id}>
            <strong>{row.document_identifier || row.document_name || row.source_content_id}</strong>
            <span>{row.status} · {row.section_count ?? 0} sections · {row.unit_count ?? 0} units</span>
            <small className="mono">{row.source_content_id}</small>
          </NavLink>)}</div>}
      </section>

      <section className="panel">
        <span className="eyebrow">PROOF BOUNDARY</span>
        <p>Shared layer coverage is evidence that one selected-runtime grammar is being applied across multiple exact sources. It is not proof that every historical Rosetta registry—actors, appeals, deadlines, references, programs, or procedures—has a current V10 owner. Those remain separately tracked resurrection obligations.</p>
      </section>

      <JsonReceipt
        value={{
          contract:'rosetta-current-v10-multi-law-proof-ui-v1',
          observed_at:data.observed_at,
          target:data.target,
          selected_runtime_population:data.source_total,
          production_rows:production,
          control_rows:controls,
        }}
        filename="rosetta-current-v10-multi-law-proof.json"
        label="Export proof receipt (JSON)"
      />
    </>}
  </div>;
}

const capabilitySurfaceConfig = {
  actors: {
    eyebrow: 'ACTOR DIRECTORY',
    title: 'Actor Directory',
    description: 'This historical capability has no independently certified selected-runtime registry.',
    gap: true,
    gapObject: 'Actor registry',
  },
  references: {
    eyebrow: 'REFERENCE EXPLORER',
    title: 'Reference Explorer',
    description: 'This historical capability has no independently certified selected-runtime registry.',
    gap: true,
    gapObject: 'Reference registry',
  },
  procedures: {
    eyebrow: 'PROCEDURE EXPLORER',
    title: 'Procedure Explorer',
    description: 'This historical capability has no independently certified selected-runtime registry.',
    gap: true,
    gapObject: 'Procedure registry',
  },
  deadlines: {
    eyebrow: 'DEADLINE TRACKER',
    title: 'Deadline Tracker',
    description: 'This historical capability has no independently certified selected-runtime registry.',
    gap: true,
    gapObject: 'Deadline registry',
  },
  appeals: {
    eyebrow: 'APPEAL PATHWAYS',
    title: 'Appeal Pathways',
    description: 'This historical capability has no independently certified selected-runtime registry.',
    gap: true,
    gapObject: 'Appeal registry',
  },
  routes: {
    eyebrow: 'ACCOUNTABILITY ROUTES',
    title: 'Route Tracker',
    description: 'Current V10 accountability representations grouped by exact source.',
    layer: 'A',
  },
  workflows: {
    eyebrow: 'WORKFLOW BOARD',
    title: 'Workflow Board',
    description: 'Current V10 workflow representations grouped by exact source.',
    layer: 'W',
  },
  programs: {
    eyebrow: 'HELP PROGRAMS',
    title: 'Help Program Finder',
    description: 'Current V10 Help representations. Contact/location registries are not inferred unless a current owner supplies them.',
    layer: 'H',
  },
  policies: {
    eyebrow: 'POLICY EXPLORER',
    title: 'Policy Explorer',
    description: 'Current exact source structure, sections, and decomposition units.',
    structure: true,
  },
  terms: {
    eyebrow: 'TERM GLOSSARY',
    title: 'Glossary + Impact Map',
    description: 'Current V10 Definition representations with exact source coordinates.',
    layer: 'D',
  },
  overrides: {
    eyebrow: 'OVERRIDE MANAGER',
    title: 'Override Manager',
    description: 'Current V10 Override representations with exact source coordinates.',
    layer: 'O',
  },
};

export function LiveCapabilitySurface({ kind }) {
  const config=capabilitySurfaceConfig[kind] || capabilitySurfaceConfig.policies;
  const { sourceId }=useParams();
  const overview=useOverview('limit=50&status=decomposed');
  const rows=overview.data?.rows || [];
  const [selectedId,setSelectedId]=useState(sourceId || null);
  const [detailState,setDetailState]=useState({loading:false,data:null,error:null});

  useEffect(()=>{
    if(sourceId && sourceId !== selectedId) {
      setSelectedId(sourceId);
      return;
    }
    if(!selectedId && rows[0]?.source_content_id) setSelectedId(rows[0].source_content_id);
  },[sourceId,selectedId,rows]);

  useEffect(()=>{
    if(!selectedId || config.gap) return;
    let mounted=true;
    setDetailState({loading:true,data:null,error:null});
    api('/api/live-rule/'+encodeURIComponent(selectedId))
      .then(data=>mounted&&setDetailState({loading:false,data,error:null}))
      .catch(error=>mounted&&setDetailState({loading:false,data:null,error}));
    return()=>{mounted=false;};
  },[selectedId,config.gap]);

  // A missing runtime response must not erase an honest capability-gap declaration.
  if(!config.gap && overview.error) return <div className="error-panel">{overview.error.message}</div>;
  const detail=detailState.data;
  const objects=detail && config.layer
    ? semanticRepresentations(detail).filter(item=>item.semantic_layer===config.layer && item.semantic_state==='classified')
    : [];

  return <div className="current-results">
    <header className="page-header">
      <div><div className="eyebrow">{config.eyebrow} / CURRENT V10</div><h1>{config.title}</h1><p>{config.description}</p></div>
    </header>

    {config.gap ? <section className="panel">
      <div className="section-heading"><div><span className="eyebrow">CURRENT AUTHORITY GAP</span><h2>{config.gapObject} not independently available from V10.</h2></div></div>
      <p>This is a confirmed historical Rosetta capability. It remains on the resurrection ledger, but this current surface will not republish pre-V10 rows or infer missing structured objects from prose.</p>
      <div className="gap-notice"><strong>CURRENT OWNER / PROJECTION REQUIRED</strong><p>When a current deterministic, source-bound projection exists, this route will consume that contract.</p></div>
    </section> : <>
      {!overview.data ? <div className="loading-panel">Loading current sources…</div> : <div className="capability-workbench">
        <aside className="panel capability-source-list">
          <span className="eyebrow">EXACT SOURCES</span>
          {rows.map(row=><button type="button" className={selectedId===row.source_content_id?'active':''} key={row.source_content_id} onClick={()=>setSelectedId(row.source_content_id)}>
            <strong>{row.document_name || row.document_identifier || row.source_content_id}</strong>
            <span className="mono">{row.document_identifier || row.source_content_id}</span>
          </button>)}
        </aside>
        <section className="panel capability-detail">
          {detailState.loading ? <div className="loading-panel">Loading exact current result…</div> : null}
          {detailState.error ? <div className="error-panel">{detailState.error.message}</div> : null}
          {detail ? <>
            <div className="section-heading">
              <div><span className="eyebrow">CURRENT SOURCE</span><h2>{detail.source?.document_name || detail.source?.document_identifier}</h2></div>
              <NavLink className="button" to={'/review/'+detail.source?.source_content_id}>Open full law →</NavLink>
            </div>
            {config.structure ? <div className="substrate-sections">
              {(detail.sections || []).map((section,index)=><article key={section.section_ord || index}>
                <strong>Section {section.section_ord ?? index+1}</strong>
                <pre>{section.heading_text || section.raw_text || section.text || JSON.stringify(section,null,2)}</pre>
              </article>)}
            </div> : objects.length ? <div className="substrate-object-list">
              {objects.map((item,index)=><article className="substrate-object" key={`${item.unit_ord}-${index}`}>
                <div className="object-topline"><strong>{item.semantic_label}</strong><span className="mono">#{item.unit_ord ?? index+1}</span></div>
                <p>{representationText(item)}</p>
                <small className="mono">section {item.section_ord ?? '—'} · source {item.raw_start ?? '—'}–{item.raw_end ?? '—'}</small>
              </article>)}
            </div> : <div className="empty-state">No current selected-runtime {config.title.toLowerCase()} objects were recorded for this exact source.</div>}
          </> : null}
        </section>
      </div>}
    </>}
  </div>;
}

function GuidedView({ detail }) {
  const result = detail.decomposition || {};
  const hold = detail.hold_receipt;
  const status = resultStatus(detail);
  const reps = semanticRepresentations(detail).filter((item) => item.semantic_state === 'classified');
  const guide = [
    ['H','Help','What assistance, protection, eligibility, benefit, or relief exists?'],
    ['W','Workflow','What must or may happen, who does it, and in what sequence?'],
    ['A','Accountability','Who is responsible, and what enforcement, consequence, or remedy appears?'],
    ['O','Override','What exception, waiver, suspension, delay, narrowing, or displacement changes the ordinary rule?'],
    ['D','Definition','What terms control how the source must be read?'],
    ['R','Non-canonical rule classifier','What additional runtime classifier output did V10 record outside the five-layer canonical contract?'],
  ];
  return <section className="panel guided-law-view">
    <div className="section-heading">
      <div><span className="eyebrow">GUIDED VIEW</span><h2>{status === 'decomposed' ? 'What this exact source says, organized by Rosetta.' : status === 'held' ? 'Rosetta stopped and preserved the reason.' : status === 'failed' ? 'The current execution failed.' : 'This source is not decomposable as current law.'}</h2></div>
    </div>
    <p>Rosetta inspected this exact source with <strong>{detail.target?.ruleset_version || 'the selected runtime'}</strong>. The source remains separate from the decomposition so every current representation can be checked against the law itself.</p>
    {hold ? <div className="gap-notice"><strong>Held at {hold.gate}</strong><p>{hold.reason}</p><p><strong>What would allow re-entry:</strong> {hold.reentry_condition}</p></div> : null}
    <div className="guided-layer-grid">
      {guide.map(([key,label,question])=>{
        const items=reps.filter(item=>item.semantic_layer===key);
        return <article key={key} className="guided-layer-card">
          <div className="object-topline"><strong>{label}</strong><span className="badge badge-neutral">{items.length}</span></div>
          <p className="guided-question">{question}</p>
          {!items.length ? <small>No current selected-runtime representation recorded.</small> :
            <ul>{items.slice(0,8).map((item,index)=><li key={`${item.unit_ord}-${index}`}>{representationText(item)}</li>)}</ul>}
          {items.length>8 ? <small>{items.length-8} additional representation(s) available in Professional/Admin.</small> : null}
        </article>;
      })}
    </div>
    {result.reason ? <p><strong>Current runtime reason:</strong> {result.reason}</p> : null}
  </section>;
}

function AdvocateView({ detail }) {
  const active = semanticRepresentations(detail).filter((item) => item.semantic_state === 'classified');
  const groups = [
    ['H','What does the law provide?'],
    ['W','What must happen?'],
    ['A','Who is accountable and what remedy or consequence appears?'],
    ['O','What changes the ordinary rule?'],
    ['D','Which definitions change how it applies?'],
    ['R','What non-canonical runtime classifier output is recorded?'],
  ];
  return <section className="panel advocate-law-view">
    <div className="section-heading"><div><span className="eyebrow">ADVOCATE VIEW</span><h2>Impact and action map</h2></div></div>
    <p className="comparison-intro">This view reorganizes the same current V10 source-bound representations around practical questions. It does not create a second interpretation.</p>
    <div className="advocate-groups">
      {groups.map(([layer,question])=>{
        const items=active.filter(item=>item.semantic_layer===layer);
        return <article key={layer}>
          <div className="object-topline"><h3>{question}</h3><span className="badge badge-neutral">{items.length}</span></div>
          {!items.length ? <p className="result-context">No current {layerLabels[layer] || layer} representation recorded.</p> :
            items.map((item,index)=><div className="advocate-item" key={`${item.unit_ord}-${index}`}>
              <p>{representationText(item)}</p>
              <small className="mono">source section {item.section_ord ?? '—'} · offsets {item.raw_start ?? '—'}–{item.raw_end ?? '—'}</small>
            </div>)}
        </article>;
      })}
    </div>
  </section>;
}

function ProfessionalView({ detail, sourceUrl, onPrint, onDownload }) {
  const src = detail.source || {};
  const result = detail.decomposition || {};
  const semantic = semanticSummary(detail);
  const representations = semantic.representations;
  const [selectedIndex,setSelectedIndex]=useState(0);
  const selected=representations[selectedIndex] || null;
  const csvName=`rosetta-${src.document_identifier || src.source_content_id || 'result'}-decomposition.csv`;

  return <>
    <section className="panel professional-review">
      <div className="section-heading">
        <div>
          <span className="eyebrow">PROFESSIONAL VIEW</span>
          <h2>Original law ↔ Rosetta decomposition</h2>
          <p className="comparison-intro">Select a Rosetta representation to highlight the exact current source span on the left. Alignment uses stored source offsets; no visual or fuzzy matching is performed.</p>
        </div>
      </div>

      <div className="page-actions">
        {sourceUrl ? <a className="button button-primary" href={sourceUrl} target="_blank" rel="noreferrer">Read official source ↗</a> : null}
        <button className="button" type="button" onClick={onPrint}>Print / save PDF report</button>
        <button className="button" type="button" onClick={onDownload}>Download human report (HTML)</button>
        <button className="button" type="button" onClick={()=>downloadCsv(csvName,semanticCsv(detail))}>Download decomposition (CSV)</button>
      </div>

      <div className="comparison-status">
        <div><span>Outcome</span><strong>{result.outcome || 'No decomposition receipt'}</strong></div>
        <div><span>Classified</span><strong>{semantic.classified}</strong></div>
        <div><span>Unresolved</span><strong>{semantic.unresolved}</strong></div>
        <div><span>Deleted spans</span><strong>{semantic.deleted}</strong></div>
        <div><span>Layers</span><strong>{Object.keys(result.layer_counts || {}).filter((key) => Number(result.layer_counts?.[key]) > 0).map((key) => layerLabels[key] || key).join(' · ') || 'None'}</strong></div>
      </div>

      <div className="comparison-workbench">
        <section className="comparison-pane comparison-source-pane">
          <div className="comparison-pane-heading">
            <span className="eyebrow">LAW AS PUBLISHED</span>
            <h3>{src.document_name || src.document_identifier || 'Preserved source'}</h3>
            <p>{selected ? `Highlighted: ${selected.semantic_label} · section ${selected.section_ord ?? '—'} · offsets ${selected.raw_start ?? '—'}–${selected.raw_end ?? '—'}` : 'Select a decomposition representation to align it.'}</p>
          </div>
          <pre className="comparison-scroll comparison-source-text">{highlightedSource(src.source_text,selected)}</pre>
        </section>

        <section className="comparison-pane comparison-decomposition-pane">
          <div className="comparison-pane-heading">
            <span className="eyebrow">ROSETTA DECOMPOSITION</span>
            <h3>Source-bound reading</h3>
            <p>Click any representation to align it to its stored source coordinates.</p>
          </div>

          {!representations.length ? <div className="comparison-empty">No source-bound decomposition is recorded for this current result.</div> :
            <div className="comparison-scroll decomposition-stream">
              {representations.map((item, index) => <button type="button" onClick={()=>setSelectedIndex(index)} className={`comparison-entry comparison-entry-button comparison-entry-${item.semantic_state} ${selectedIndex===index?'active':''}`} key={`${item.unit_ord}-${item.semantic_layer || item.semantic_state}-${index}`}>
                <div className="comparison-entry-heading">
                  <h4>{item.semantic_label}</h4>
                  <span className={`badge badge-${item.semantic_state === 'classified' ? 'good' : item.semantic_state === 'unresolved' ? 'warn' : 'neutral'}`}>{item.semantic_state}</span>
                </div>
                <p className="comparison-reading">{representationText(item)}</p>
                <small className="mono">section {item.section_ord ?? '—'} · source {item.raw_start ?? '—'}–{item.raw_end ?? '—'}</small>
              </button>)}
            </div>}
        </section>
      </div>
    </section>

    <JsonReceipt
      value={{
        contract: detail.contract,
        observed_at: detail.observed_at,
        target: detail.target,
        source: src,
        decomposition: detail.decomposition,
        sections: detail.sections,
        units: detail.units,
        hold_receipt: detail.hold_receipt,
      }}
      filename={`rosetta-professional-${src.source_content_id || 'result'}.json`}
      label="Machine result (JSON)"
    />
  </>;
}

function CurrentReceiptControl({ detail }) {
  const src=detail.source || {};
  const result=detail.decomposition || {};
  const target=detail.target || {};
  const readSha=target.read_contract_sha256 || target.read_contract_sha || null;
  return <section className="panel receipt-control">
    <div className="section-heading">
      <div><span className="eyebrow">CURRENT RECEIPT CONTROL</span><h2>{src.document_identifier || src.document_name || src.source_content_id}</h2></div>
      <span className={'badge badge-'+tone(resultStatus(detail))}>{statusLabel[resultStatus(detail)] || resultStatus(detail)}</span>
    </div>
    <div className="receipt-control-grid">
      <article><span>Source identity</span><strong className="mono">{src.source_content_id || '—'}</strong></article>
      <article><span>Source content SHA</span><strong className="mono">{src.source_content_hash || result.source_content_hash || '—'}</strong></article>
      <article><span>Ruleset</span><strong>{result.ruleset_version || target.ruleset_version || '—'}</strong></article>
      <article><span>Ruleset SHA</span><strong className="mono">{result.ruleset_sha256 || target.ruleset_sha256 || '—'}</strong></article>
      <article><span>Read contract SHA</span><strong className="mono">{readSha || 'Not exposed'}</strong></article>
      <article><span>Provider document</span><strong className="mono">{result.docket_provider_document_id || 'Not recorded'}</strong></article>
      <article><span>Completed / observed</span><strong>{result.created_at ? new Date(result.created_at).toLocaleString() : detail.observed_at ? new Date(detail.observed_at).toLocaleString() : '—'}</strong></article>
      <article><span>Configuration hash</span><strong>Not exposed by current V10 read contract</strong></article>
      <article><span>Output content hash</span><strong>Not exposed by current V10 read contract</strong></article>
    </div>
    <div className="receipt-layer-grid">
      {Object.entries(layerLabels).map(([key,label])=>{
        const count=Number(result.layer_counts?.[key] || 0);
        return <article key={key}><span>{label}</span><strong>{count}</strong><small>{count>0?'POPULATED':'NO CURRENT REPRESENTATION'}</small></article>;
      })}
    </div>
  </section>;
}

function AdminView({ detail }) {
  const src=detail.source || {};
  const result=detail.decomposition;
  return <>
    <CurrentReceiptControl detail={detail}/>
    <Target value={detail}/>
    <section className="panel">
      <span className="eyebrow">SOURCE RECEIPT</span>
      <dl>
        <dt>Source content ID</dt><dd className="mono">{src.source_content_id}</dd>
        <dt>Source document ID</dt><dd className="mono">{src.source_document_id}</dd>
        <dt>Source hash</dt><dd className="mono">{src.source_content_hash}</dd>
        <dt>Media type</dt><dd>{src.media_type || '—'}</dd>
        <dt>Source URL</dt><dd>{src.source_url || '—'}</dd>
        <dt>Version</dt><dd>{src.source_version || '—'}</dd>
        <dt>Ruleset SHA</dt><dd className="mono">{detail.target?.ruleset_sha256}</dd>
      </dl>
    </section>
    {result ? <section className="panel">
      <span className="eyebrow">DECOMPOSITION RECEIPT</span>
      <dl>
        <dt>Outcome</dt><dd>{result.outcome}</dd>
        <dt>Underlying storage rows</dt><dd>{result.unit_count ?? 0}</dd>
        <dt>Sections</dt><dd>{result.section_count ?? 0}</dd>
        <dt>Layers</dt><dd className="mono">{JSON.stringify(result.layer_counts || {})}</dd>
        <dt>Flags</dt><dd className="mono">{JSON.stringify(result.flags || {})}</dd>
      </dl>
      {result.reason ? <p>{result.reason}</p> : null}
    </section> : null}
    {detail.hold_receipt ? <section className="panel"><span className="eyebrow">HOLD RECEIPT</span><h2>{detail.hold_receipt.gate}</h2><p>{detail.hold_receipt.reason}</p><p><strong>Re-entry:</strong> {detail.hold_receipt.reentry_condition}</p></section> : null}
    {detail.failure_receipts?.length ? <JsonReceipt value={detail.failure_receipts} filename={`rosetta-failures-${src.source_content_id}.json`} label="Failure receipts (JSON)" /> : null}
    {detail.markup_events?.length ? <JsonReceipt value={detail.markup_events} filename={`rosetta-operative-events-${src.source_content_id}.json`} label="Operative-text events (JSON)" /> : null}
    <JsonReceipt value={detail} filename={`rosetta-admin-${src.source_content_id || 'result'}.json`} label="Complete administrative execution receipt (JSON)" open />
  </>;
}

export function LiveDetail() {
  const {sourceId}=useParams();
  const {mode}=useVisibilityMode();
  const [state,setState]=useState({loading:true});
  useEffect(()=>{let active=true;api('/api/live-rule/'+encodeURIComponent(sourceId)).then(data=>active&&setState({loading:false,data})).catch(error=>active&&setState({loading:false,error}));return()=>{active=false;};},[sourceId]);
  if(state.error) return <div className="error-panel">{state.error.message}</div>;
  if(!state.data) return <div className="loading-panel">Loading current result…</div>;

  const detail=state.data;
  const src=detail.source || {};
  const status=resultStatus(detail);
  const isAdmin=mode==='admin';
  const isProfessional=mode==='professional' || isAdmin;
  const isAdvocate=mode==='advocate' || isProfessional;
  const sourceUrl=safeSourceUrl(src.source_url);
  const report=printableReport(detail,isAdmin);
  const reportName=`rosetta-detailed-${src.document_identifier || src.source_content_id || 'result'}.html`;

  return <div className="current-results">
    <div className="page-actions result-back"><NavLink to="/review">← Current results</NavLink></div>
    <header className="page-header current-law-identity">
      <div>
        <div className="eyebrow">CURRENT LAW / EXACT SOURCE</div>
        <h1>{src.document_name || src.document_identifier || src.source_content_id}</h1>
        <div className="law-identity-strip">
          <span><strong>{sourceJurisdiction(src) || 'Jurisdiction not recorded'}</strong><small>jurisdiction</small></span>
          <span><strong>{src.document_identifier || 'Identifier not recorded'}</strong><small>bill / source identifier</small></span>
          <span><strong>{src.source_version || 'Version not recorded'}</strong><small>source version</small></span>
          <span><strong>{statusLabel[status] || String(status).toUpperCase()}</strong><small>Rosetta state</small></span>
        </div>
        <p className="mono">selected runtime {detail.target?.ruleset_version} · source {src.source_content_id}</p>
      </div>
      <span className={'badge badge-'+tone(status)}>{statusLabel[status] || String(status).toUpperCase()}</span>
    </header>

    <nav className="law-context-rail" aria-label="Current law capability views">
      <NavLink to={'/layers/source/'+encodeURIComponent(src.source_content_id)}>Layers</NavLink>
      <NavLink to={'/programs/'+encodeURIComponent(src.source_content_id)}>Help</NavLink>
      <NavLink to={'/workflows/'+encodeURIComponent(src.source_content_id)}>Workflow</NavLink>
      <NavLink to={'/routes/'+encodeURIComponent(src.source_content_id)}>Accountability</NavLink>
      <NavLink to={'/overrides/'+encodeURIComponent(src.source_content_id)}>Overrides</NavLink>
      <NavLink to={'/terms/'+encodeURIComponent(src.source_content_id)}>Definitions</NavLink>
      <NavLink to={'/policies/'+encodeURIComponent(src.source_content_id)}>Policy</NavLink>
    </nav>

    <div className="page-actions">
      {sourceUrl ? <a className="button button-primary" href={sourceUrl} target="_blank" rel="noreferrer">Read official source ↗</a> : null}
    </div>

    <GuidedView detail={detail}/>
    {isAdvocate ? <AdvocateView detail={detail}/> : null}
    {isProfessional ? <ProfessionalView detail={detail} sourceUrl={sourceUrl} onPrint={() => printHtml(report)} onDownload={() => downloadHtml(reportName,report)} /> : null}
    {isAdmin ? <AdminView detail={detail}/> : null}
  </div>;
}
